import React, { useEffect, useState, useCallback } from 'react'
import { Box, Text, Button, Select, Stack, Loader } from '@mantine/core'
import { observer } from 'mobx-react-lite'
import agent from '../app/api/agent'
import { useStore } from '../app/stores/store'
import { extractThumbnailFromVideo } from './ThumbnailExtractor'

const ShootsDetailsScreen = observer(function ShootsDetailsScreen () {
  const { editorStore, uploadDrawerStore } = useStore()
  const [firearmOptions, setFirearmOptions] = useState<{ value: string; label: string }[]>([])
  const [cartridgeOptions, setCartridgeOptions] = useState<{ value: string; label: string }[]>([])
  const [listsLoading, setListsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setListsLoading(true)
    void (async () => {
      try {
        const [fm, cs] = await Promise.all([
          agent.FirearmModels.list({ pageSize: 40 }),
          agent.CartridgeSpecs.list({ pageSize: 80 })
        ])
        if (cancelled) return
        setFirearmOptions(
          fm.items.map((m) => ({
            value: m.id,
            label: `${m.make} ${m.model}`.trim()
          }))
        )
        setCartridgeOptions(
          cs.items.map((c) => ({
            value: c.id,
            label: (c.shortName || c.name || '').trim() || c.id
          }))
        )
      } catch {
        if (!cancelled) {
          setFirearmOptions([])
          setCartridgeOptions([])
        }
      } finally {
        if (!cancelled) setListsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handlePost = useCallback(async () => {
    const photoUrl = editorStore.photoUrl
    const mediaType = editorStore.mediaType
    if (!photoUrl || !mediaType) return

    let thumbnailBlob: Blob | undefined
    if (mediaType === 'video') {
      try {
        thumbnailBlob = await extractThumbnailFromVideo(photoUrl)
      } catch {
        /* optional thumbnail */
      }
    }
    await editorStore.postShoot(() => void uploadDrawerStore.afterSuccessfulShootFromEditor(), thumbnailBlob)
  }, [editorStore, uploadDrawerStore])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        backgroundColor: 'var(--gs-bg-2)',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid var(--gs-border)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid var(--gs-border)',
          flexShrink: 0
        }}
      >
        <Button variant="subtle" size="sm" compact onClick={() => uploadDrawerStore.backToEditor()} sx={{ fontWeight: 600 }}>
          &lt; Back
        </Button>
        <Button
          variant="filled"
          color="orange"
          size="sm"
          radius="xl"
          loading={editorStore.isPosting}
          disabled={editorStore.isPosting || !editorStore.canPost}
          onClick={() => void handlePost()}
          px="lg"
        >
          Post
        </Button>
      </Box>

      <Box sx={{ padding: 12, flex: 1, minHeight: 0, position: 'relative' }}>
        {listsLoading
          ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 'xl' }}>
            <Loader size="sm" />
          </Box>
            )
          : (
          <Stack spacing="xs">
            <Select
              label="Firearm model (optional)"
              placeholder="None"
              searchable
              clearable
              size="xs"
              data={firearmOptions}
              value={editorStore.linkedFirearmModelId}
              onChange={(v) => editorStore.setLinkedFirearmModelId(v)}
            />
            <Select
              label="Cartridge (optional)"
              placeholder="None"
              searchable
              clearable
              size="xs"
              data={cartridgeOptions}
              value={editorStore.linkedCartridgeSpecId}
              onChange={(v) => editorStore.setLinkedCartridgeSpecId(v)}
            />
          </Stack>
            )}

        {editorStore.error && (
          <Text size="sm" c="red" mt="xs">
            {editorStore.error}
          </Text>
        )}
      </Box>
    </Box>
  )
})

export default ShootsDetailsScreen
